import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Download } from 'lucide-react';

interface Machine {
  id: string;
  name: string;
  serialNumber: string;
  compteurInitial: number;
  dateAchat: string;
  garantieMois: number;
  brand: string;
}

const MachinesTable = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [garantieFilter, setGarantieFilter] = useState<string>('all');

  // Generate placeholder data
  const machines: Machine[] = [
    {
      id: '1',
      name: 'Bizhub C368',
      serialNumber: 'KM368-' + Math.random().toString(36).substr(2, 8).toUpperCase(),
      compteurInitial: Math.floor(Math.random() * (100000 - 50000) + 50000),
      dateAchat: '2024-01-15', // More than 6 months ago
      garantieMois: 6,
      brand: 'Konica Minolta'
    },
    {
      id: '2',
      name: 'Bizhub C250i',
      serialNumber: 'KM250-' + Math.random().toString(36).substr(2, 8).toUpperCase(),
      compteurInitial: Math.floor(Math.random() * (100000 - 50000) + 50000),
      dateAchat: '2024-07-20', // Previous month
      garantieMois: 6,
      brand: 'Konica Minolta'
    },
    {
      id: '3',
      name: 'Bizhub C3350i',
      serialNumber: 'KM335-' + Math.random().toString(36).substr(2, 8).toUpperCase(),
      compteurInitial: Math.floor(Math.random() * (100000 - 50000) + 50000),
      dateAchat: '2024-02-10', // More than 6 months ago
      garantieMois: 3,
      brand: 'Konica Minolta'
    }
  ];

  const getGarantieStatus = (dateAchat: string, garantieMois: number) => {
    const purchaseDate = new Date(dateAchat);
    const now = new Date();
    const monthsDiff = (now.getFullYear() - purchaseDate.getFullYear()) * 12 + (now.getMonth() - purchaseDate.getMonth());
    
    if (monthsDiff < garantieMois) {
      return { status: 'Sous garantie', variant: 'default' as const };
    } else {
      return { status: 'Hors garantie', variant: 'destructive' as const };
    }
  };

  const filteredMachines = useMemo(() => {
    return machines.filter(machine => {
      const matchesSearch = machine.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           machine.serialNumber.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (garantieFilter === 'all') return matchesSearch;
      
      const garantieStatus = getGarantieStatus(machine.dateAchat, machine.garantieMois);
      const isUnderWarranty = garantieStatus.status === 'Sous garantie';
      
      if (garantieFilter === 'sous-garantie') return matchesSearch && isUnderWarranty;
      if (garantieFilter === 'hors-garantie') return matchesSearch && !isUnderWarranty;
      
      return matchesSearch;
    });
  }, [searchTerm, garantieFilter]);

  const handleExport = () => {
    const csvContent = [
      ['Nom', 'Numéro de série', 'Compteur Initial', 'Date d\'achat', 'Marque', 'Statut de garantie'],
      ...filteredMachines.map(machine => [
        machine.name,
        machine.serialNumber,
        machine.compteurInitial.toString(),
        new Date(machine.dateAchat).toLocaleDateString('fr-FR'),
        machine.brand,
        getGarantieStatus(machine.dateAchat, machine.garantieMois).status
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'mes-machines.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mes Machines</CardTitle>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom ou numéro de série..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full sm:w-80"
              />
            </div>
            <Select value={garantieFilter} onValueChange={setGarantieFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filtrer par garantie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les garanties</SelectItem>
                <SelectItem value="sous-garantie">Sous garantie</SelectItem>
                <SelectItem value="hors-garantie">Hors garantie</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleExport} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Exporter
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Numéro de série</TableHead>
                <TableHead>Compteur Initial</TableHead>
                <TableHead>Date d'achat</TableHead>
                <TableHead>Statut de garantie</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMachines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    Aucune machine trouvée.
                  </TableCell>
                </TableRow>
              ) : (
                filteredMachines.map((machine) => {
                  const garantieStatus = getGarantieStatus(machine.dateAchat, machine.garantieMois);
                  return (
                    <TableRow key={machine.id}>
                      <TableCell className="font-medium">
                        <div>
                          <p>{machine.name}</p>
                          <p className="text-sm text-muted-foreground">{machine.brand}</p>
                        </div>
                      </TableCell>
                      <TableCell>{machine.serialNumber}</TableCell>
                      <TableCell>{machine.compteurInitial.toLocaleString('fr-FR')}</TableCell>
                      <TableCell>{new Date(machine.dateAchat).toLocaleDateString('fr-FR')}</TableCell>
                      <TableCell>
                        <Badge variant={garantieStatus.variant}>
                          {garantieStatus.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default MachinesTable;